import React, { useEffect, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { FacilityInformation, FacilityFarmCapacityInformation, FacilityProcessDetails } from "../../../sc-models/facility-product-dto";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { useParams } from "react-router-dom";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { FARM_CODES, SUPPLY_CHAIN_CODES, ALL_FARM_CATEGORIES, FARM_RM_CODES, FarmCategoryUtils } from "../../../constants/farm-categories";
import useNotification from "../../../../../../shared/hooks/useNotification";

interface FarmCapacityInfoProps {
  formRef:any,
  facilityData: FacilityInformation;
  facilityFarmCapacityInfo: FacilityFarmCapacityInformation[]; // Expecting an array
  facilityStandard: string[]; // Added for conditional validation
  facilityProcessInfo: FacilityProcessDetails[]; // Added for validation
  setScSubsequentFacilityButtonFlag: React.Dispatch<React.SetStateAction<boolean>>; // Added for button control
  setFacilityFarmCapacityInfo: React.Dispatch<
    React.SetStateAction<FacilityFarmCapacityInformation[]>
  >;
  isSaving?: boolean;
  isLoading?: boolean;
}

const FacilityFarmCapacityInfo: React.FC<FarmCapacityInfoProps> = ({
  formRef,
  facilityData,
  facilityFarmCapacityInfo,
  facilityStandard,
  facilityProcessInfo,
  setScSubsequentFacilityButtonFlag,
  setFacilityFarmCapacityInfo,
  isSaving,
  isLoading,
}) => {
  const { updateField } = useFormActions("sc-facilitydetails");
  const [data, setData] = useState<FacilityFarmCapacityInformation[]>(
    facilityFarmCapacityInfo
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<FacilityFarmCapacityInformation[]>(
    facilityFarmCapacityInfo
  ); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [rawMaterialCode,setRawMaterialCode] = useState<any[]>([]);
  const [filteredRawMaterialCode, setFilteredRawMaterialCode] = useState<any[]>([]);
  const { isRestrictPopUpSave, setRestrictPopUpSave, setFacilityProcessCategoryCodes}=useTransactionCertificateStore()
  
  const { SC_ID } = useParams();
  const { executeGet } = useApi();

  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const { notify } = useNotification();
  /** Tracks which editing-row fields have failed validation (highlighted red on save click). */
  const [rowErrors, setRowErrors] = useState<Record<string, boolean>>({});
  const [rowErrorMessages, setRowErrorMessages] = useState<Record<string, string>>({});


  // Function to compute if farm number is required (from facility-information.tsx)
  const computeFarmNumRequired = (codes: string[]): boolean => {
    const farmCodes = FarmCategoryUtils.getFarmCodesSet();
    const supplyChainCodes = FarmCategoryUtils.getSupplyChainCodesSet();

    const codeSet = new Set(codes);
    const hasFarmCode = codes.some((code) => farmCodes.has(code));

    if (!hasFarmCode) return false;

    const nonFarmCodes = codes.filter((code) => !farmCodes.has(code));

    // Case 1: Only farm codes
    if (nonFarmCodes.length === 0) return true;

    // Case 2: Farm + supply chain codes only
    const allNonFarmAreSupplyChain = nonFarmCodes.every((code) =>
      supplyChainCodes.has(code)
    );
    if (allNonFarmAreSupplyChain) return true;

    // Case 3: Farm + other codes (excluding supply chain)
    const hasOtherNonSupplyChain = nonFarmCodes.some(
      (code) => !supplyChainCodes.has(code)
    );
    if (hasOtherNonSupplyChain) return true;

    return false;
  };

  // Check if farm number is required based on facility standards and facility type
  const facilityType = facilityData.facilityType;
  const requiresFarmCapacity = facilityType === "MF" || facilityType === "FA";
  const isFarmNumRequired = requiresFarmCapacity && computeFarmNumRequired(facilityStandard);

  // Farm categories that require farm animals data
  const farmCategories = ALL_FARM_CATEGORIES;

  const validateFarmRequirements = () => {
    // The previous logic incorrectly disabled the Save button if there were no animal counts,
    // which broke plant-based farms (like Cotton).
    // Let the validateBeforePost function in index.tsx handle form-level validation and show
    // helpful error toasts to the user if data is missing, rather than silently disabling the button.
    setScSubsequentFacilityButtonFlag(true);
    return true;
  };

  useEffect(() => {
      setRestrictPopUpSave(false);
      // Initialize validation on component mount
      validateFarmRequirements();
    return (()=>{
      setRestrictPopUpSave(false);
    })
  }, [])

  // Run validation whenever relevant data changes
  useEffect(() => {
    validateFarmRequirements();
  }, [data, facilityProcessInfo]); // Use local data state instead of prop

  const validationSchema = Yup.object().shape({
    facilityFarmRawMaterialCode: Yup.string().required("Please enter farm raw material code"),
    facilityFarmRawMaterialCapacity: Yup.string().when("facilityFarmRawMaterialCode", {
      is: (val: any) => ['RM0083', 'RM0330', 'RM0064', 'RM0007', 'RM0032', 'RM0039'].includes(val),
      then: (schema) => schema.required("Facility Farm Raw Material Capacity is required"),
      otherwise: (schema) => schema.nullable(),
    }).matches(/^\d+(\.\d+)?$/, { message: "Must be a valid number", excludeEmptyString: true }),
    facilityFarmRawMaterialNumberOfAnimals: Yup.string().when("facilityFarmRawMaterialCode", {
      is: (val: any) => ['RM0083', 'RM0330', 'RM0064', 'RM0007'].includes(val),
      then: (schema) => schema.required("Facility Farm Raw Material Number Of Animals is required"),
      otherwise: (schema) => schema.nullable(),
    }).matches(/^\d+$/, { message: "Must be an integer", excludeEmptyString: true }),
  });


  // Load raw material master codes — dropdown shows ONLY farm-based RM codes.
  // The full list is kept in rawMaterialCode for label resolution (handleSaveRow).
  useEffect(() => {
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItServiceV4.getRawMaterialCodes();
        const farmRmSet = new Set<string>(FARM_RM_CODES);
        const allCodes = result.data.map((t: any) => ({ value: t.code_1, label: `${t.description_1} (${t.code_1})` }));
        // Only expose farm-based RM codes in the selector — non-farm codes are hidden.
        const farmOnlyCodes = allCodes.filter((opt: any) => farmRmSet.has(opt.value));
        setRawMaterialCode(allCodes);           // full list kept for label lookup
        setFilteredRawMaterialCode(farmOnlyCodes); // farm-only list for the dropdown

        // Back-fill descriptions for existing rows loaded from the backend that
        // have an RM code but no description (e.g. saved before this field existed).
        const optionMap = new Map<string, string>(allCodes.map((o: any) => [o.value, o.label]));
        setData((prevData) =>
          prevData.map((row) => {
            if (row.facilityFarmRawMaterialCode && !row.facilityFarmRawMaterialCodeDescription) {
              const fullLabel = optionMap.get(row.facilityFarmRawMaterialCode) ?? '';
              const description = fullLabel.replace(/\s*\([^)]+\)\s*$/, '').trim();
              return { ...row, facilityFarmRawMaterialCodeDescription: description };
            }
            return row;
          })
        );
      } catch (err) {
        console.error('Error fetching RM codes', err);
      }
    };
    getRawMaterialCodes();
  }, []);

  useEffect(() => {
    updateField("facilityProcessDetails", data)
  }, [data])

  const formik = useFormik<FacilityFarmCapacityInformation>({
    initialValues: {
      facilityFarmRawMaterialCode: "",
      facilityFarmRawMaterialCapacity: "",
      facilityFarmRawMaterialCodeDescription:"",
      facilityFarmRawMaterialNumberOfAnimals: ""
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  // Strip trailing " (CODE)" from a label e.g. "Wool - Responsible (RWS) (RM0083)" → "Wool - Responsible (RWS)"
  const stripCode = (label: string) => label.replace(/\s*\([^)]+\)\s*$/, '').trim();

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    if (columnId === 'facilityFarmRawMaterialCode') {
      formik.setFieldValue(columnId, value);
      const selectedOption = filteredRawMaterialCode.find((opt: any) => opt.value === value);
      if (selectedOption) {
        const description = stripCode(selectedOption.label);
        formik.setFieldValue('facilityFarmRawMaterialCodeDescription', description);
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex
              ? { ...row, [columnId]: value, facilityFarmRawMaterialCodeDescription: description }
              : row
          )
        );
      } else {
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, [columnId]: value } : row
          )
        );
      }
    } else {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    }
  };

  const handleAddNewRow = () => {
    const newRow: FacilityFarmCapacityInformation = {
      facilityFarmRawMaterialCode: "",
      facilityFarmRawMaterialCapacity: "",
      facilityFarmRawMaterialCodeDescription:"",
      facilityFarmRawMaterialNumberOfAnimals: ""
    };
    setRestrictPopUpSave(true);
    setRowErrors({});
    setRowErrorMessages({});
    const newData = [...data,newRow];
    setData(newData);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    setData(tableData);
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    setRowErrors({});
    setRowErrorMessages({});
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === null) return;

    const currentRowData = data[editingRowIndex];
    const newErrors: Record<string, boolean> = {};
    const newErrorMessages: Record<string, string> = {};

    const capacityStr = String(currentRowData?.facilityFarmRawMaterialCapacity ?? '').trim();
    const animalsStr  = String(currentRowData?.facilityFarmRawMaterialNumberOfAnimals ?? '').trim();
    const rmCode      = String(currentRowData?.facilityFarmRawMaterialCode ?? '');

    // --- RM Code required ---
    if (!rmCode) {
      newErrors.facilityFarmRawMaterialCode = true;
      newErrorMessages.facilityFarmRawMaterialCode = 'Raw material code is required';
    } else {
      // --- Duplicate RM code check ---
      const duplicateExists = data.some((row, index) =>
        index !== editingRowIndex &&
        row.facilityFarmRawMaterialCode &&
        String(row.facilityFarmRawMaterialCode) === rmCode
      );
      if (duplicateExists) {
        newErrors.facilityFarmRawMaterialCode = true;
        newErrorMessages.facilityFarmRawMaterialCode = 'Raw material code must be unique across all rows';
      }
    }

    // --- Capacity required/format ---
    const mandatoryCapacityCodes = ['RM0083', 'RM0330', 'RM0064', 'RM0007', 'RM0032', 'RM0039'];
    const capacityNum = Number(capacityStr);
    const isCapacityMandatory = mandatoryCapacityCodes.includes(rmCode);
    if (!capacityStr && isCapacityMandatory) {
      newErrors.facilityFarmRawMaterialCapacity = true;
      newErrorMessages.facilityFarmRawMaterialCapacity = 'Capacity is required for this material';
    } else if (capacityStr && !/^\d+$/.test(capacityStr)) {
      newErrors.facilityFarmRawMaterialCapacity = true;
      newErrorMessages.facilityFarmRawMaterialCapacity = 'Must be a valid positive integer';
    } else if (capacityStr && isCapacityMandatory && capacityNum <= 0) {
      newErrors.facilityFarmRawMaterialCapacity = true;
      newErrorMessages.facilityFarmRawMaterialCapacity = 'Value must be greater than 0 (zero and negative values are not allowed)';
    }

    // --- Animals required/format ---
    const mandatoryAnimalsCodes = ['RM0083', 'RM0330', 'RM0064', 'RM0007'];
    const animalsNum = Number(animalsStr);
    const isAnimalsMandatory = mandatoryAnimalsCodes.includes(rmCode);
    if (!animalsStr && isAnimalsMandatory) {
      newErrors.facilityFarmRawMaterialNumberOfAnimals = true;
      newErrorMessages.facilityFarmRawMaterialNumberOfAnimals = 'Number of animals is required for this material';
    } else if (animalsStr && !/^\d+$/.test(animalsStr)) {
      newErrors.facilityFarmRawMaterialNumberOfAnimals = true;
      newErrorMessages.facilityFarmRawMaterialNumberOfAnimals = 'Must be a valid integer';
    } else if (animalsStr && isAnimalsMandatory && animalsNum <= 0) {
      newErrors.facilityFarmRawMaterialNumberOfAnimals = true;
      newErrorMessages.facilityFarmRawMaterialNumberOfAnimals = 'Value must be greater than 0 (zero and negative values are not allowed)';
    }

    // If any field is invalid — highlight them all and stop
    if (Object.values(newErrors).some(Boolean)) {
      setRowErrors(newErrors);
      setRowErrorMessages(newErrorMessages);
      notify('Please fix the highlighted errors', 'error');
      
      // Auto-scroll to error
      setTimeout(() => {
        const errorElement = document.querySelector('.Mui-error, [aria-invalid="true"]');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    // All valid — clear highlights and save
    setRowErrors({});
    setRowErrorMessages({});
    const updatedRow = {
      ...formik.values,
      facilityFarmRawMaterialCodeDescription: rawMaterialCode?.find((rm: any) => rm?.value === formik?.values?.facilityFarmRawMaterialCode)?.label
    };

    const newData = data?.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);
    setFacilityFarmCapacityInfo(newData);
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  // Helper function to generate helper text for farm animals field
  const getFarmAnimalsHelperText = () => {
    const selectedStandards = facilityStandard || [];
    const rafStandards = selectedStandards.filter(
      (standard: string) => ["RMS", "RAS", "RWS"].includes(standard)
    );
    const hasRDS = selectedStandards.includes("RDS");

    if (rafStandards.length > 0 || hasRDS) {
      let message = "Submit ";

      if (rafStandards.length > 0) {
        message += `number of animals for ${rafStandards.join(", ")}`;
      }

      if (rafStandards.length > 0 && hasRDS) {
        message += " and ";
      }

      if (hasRDS) {
        message += "number of birds for RDS";
      }

      return message;
    }
    return "";
  };

  const columns: EditableColumnDef<FacilityFarmCapacityInformation>[] = [
    {
      accessorKey: "facilityFarmRawMaterialCode",
      header: "Facility Farm Raw Material Code",
      Header: <span> {requiresFarmCapacity && <i style={{ color: 'red' }}>* </i>}Farm RM Code</span>,
      size: 200,
      minSize: 160,
      maxSize: 250,
      grow: false,
        renderEditCell: ({ value, onChange }) => (
          <Tooltip
            open={!!rowErrors.facilityFarmRawMaterialCode}
            title={rowErrorMessages.facilityFarmRawMaterialCode ?? ''}
            arrow
            placement="top"
            componentsProps={{ tooltip: { sx: { bgcolor: '#d32f2f', fontSize: '0.75rem' } } }}
          >
            <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <CustomSelect
                name="facilityFarmRawMaterialCode"
                value={value}
                placeholder="Enter value"
                options={filteredRawMaterialCode}
                required={requiresFarmCapacity}
                enableSearch={true}
                size="small"
                showCodeOnlyWhenSelected={true}
                error={rowErrors.facilityFarmRawMaterialCode}
                sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                onChange={(e) => {
                  onChange(e.target.value);
                  if (rowErrors.facilityFarmRawMaterialCode) {
                    setRowErrors(prev => ({ ...prev, facilityFarmRawMaterialCode: false }));
                  }
                }}
              />
            </span>
          </Tooltip>
        ),
    },
    {
      accessorKey: "facilityFarmRawMaterialCodeDescription",
      header: "Description",
      Header: <span>Description</span>,
      size: 220,
      minSize: 160,
      maxSize: 320,
      grow: true,
    },
    {
      accessorKey: "facilityFarmRawMaterialCapacity",
      header: "Facility Farm Raw Material Capacity",
      Header: <span> {(data.some((row: any) => ['RM0083', 'RM0330', 'RM0064', 'RM0007', 'RM0032', 'RM0039'].includes(row?.facilityFarmRawMaterialCode))) && <i style={{ color: 'red' }}>* </i>}Farm Capacity</span>,
      size: 180,
      minSize: 150,
      maxSize: 220,
      grow: false,
      renderEditCell: ({ value, onChange }) => (
          <Tooltip
            open={!!rowErrors.facilityFarmRawMaterialCapacity}
            title={rowErrorMessages.facilityFarmRawMaterialCapacity ?? ''}
            arrow
            placement="top"
            componentsProps={{ tooltip: { sx: { bgcolor: '#d32f2f', fontSize: '0.75rem' } } }}
          >
            <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <CustomTextField
                name="facilityFarmRawMaterialCapacity"
                required={['RM0083', 'RM0330', 'RM0064', 'RM0007', 'RM0032', 'RM0039'].includes(data[editingRowIndex]?.facilityFarmRawMaterialCode)}
                value={value}
                size="small"
                type="number"
                inputProps={{ min: 1, step: 1 }}
                error={rowErrors.facilityFarmRawMaterialCapacity}
                sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (['.', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                }}
                onChange={(e: any) => {
                  const raw = e.target.value;
                  if (raw === '' || /^\d+$/.test(raw)) {
                    onChange(raw);
                    if (rowErrors.facilityFarmRawMaterialCapacity) {
                      setRowErrors(prev => ({ ...prev, facilityFarmRawMaterialCapacity: false }));
                    }
                  }
                }}
                placeholder="Enter value"
              />
            </span>
          </Tooltip>
        ),
    },
    {
      accessorKey: "facilityFarmRawMaterialNumberOfAnimals",
      header: "Facility Farm Raw Material Number Of Animals",
      Header: <span> {(data.some((row: any) => ['RM0083', 'RM0330', 'RM0064', 'RM0007'].includes(row?.facilityFarmRawMaterialCode))) && <i style={{ color: 'red' }}>* </i>}Farm No. of Animals</span>,
      size: 180,
      minSize: 150,
      maxSize: 220,
      grow: false,
      renderEditCell: ({ value, onChange }) => (
          <Tooltip
            open={!!rowErrors.facilityFarmRawMaterialNumberOfAnimals}
            title={rowErrorMessages.facilityFarmRawMaterialNumberOfAnimals ?? ''}
            arrow
            placement="top"
            componentsProps={{ tooltip: { sx: { bgcolor: '#d32f2f', fontSize: '0.75rem' } } }}
          >
            <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <CustomTextField
                name="facilityFarmRawMaterialNumberOfAnimals"
                required={['RM0083', 'RM0330', 'RM0064', 'RM0007'].includes(data[editingRowIndex]?.facilityFarmRawMaterialCode)}
                value={value}
                size="small"
                error={rowErrors.facilityFarmRawMaterialNumberOfAnimals}
                sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                onChange={(e) => {
                  onChange(e.target.value);
                  if (rowErrors.facilityFarmRawMaterialNumberOfAnimals) {
                    setRowErrors(prev => ({ ...prev, facilityFarmRawMaterialNumberOfAnimals: false }));
                  }
                }}
                placeholder="Enter Number of Animals"
              />
            </span>
          </Tooltip>
        ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setFacilityFarmCapacityInfo(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton disabled={!formik.isValid || isRestrictPopUpSave || isSaving || isLoading} onClick={handleAddNewRow} sx={{ mb: 2 }}>
            Add Facility Farm Capacity Information
          </StyledButton>
        </Box>
        {showWarning && (
          <Box sx={{ p: 2, mb: 2, bgcolor: '#FFF3E0', borderRadius: 1, border: '1px solid #FFB74D' }}>
            <Typography color="warning.dark">
              {warningMessage}
            </Typography>
          </Box>
        )}
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enableEditing={!isSaving && !isLoading}
          isSaving={isSaving}
        />
      </Box>
    </div>
  );
};

export default FacilityFarmCapacityInfo;
